/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import debug from 'debug';

import { displayException, mapCRSFromConfig, parseCRSName } from 'sdi/app';
import { initialTableState } from 'sdi/components/table';
import { defaultTableState } from 'sdi/components/table2';
import {
    defaultInteraction,
    defaultPrintRequest,
    defaultPrintResponse,
} from 'sdi/map';
import 'sdi/polyfill';
import { defaultToolGeocoder } from 'sdi/ports/geocoder';
import { defaultShape } from 'sdi/shape';
import { configure } from 'sdi/shape/functions';
import type { IShape } from 'sdi/shape/types';
import {
    AppConfigIO,
    getMessage,
    initialStreamingState,
    remoteNone,
    source,
} from 'sdi/source';

import App from './app';
import { defaultPrintState } from './components/print';
import './locale';
import './shape';
import { defaultHomeConfigTile } from './shape/types';
import { getCenter } from 'ol/extent';

const logger = debug('sdi:index');

export const main = (SDI: unknown) => {
    AppConfigIO.decode(SDI).fold(
        errors => {
            const textErrors = errors.map(e => getMessage(e.value, e.context));
            displayException(textErrors.join('\n'));
        },
        config => {
            const initialState: IShape = {
                'app/codename': 'view',
                ...defaultShape(config),
                'app/user': config.user,
                'app/root': config.root,
                'app/api-root': config.api,
                'app/csrf': config.csrf,
                'app/lang': 'fr',
                'app/layout': 'map-navigator-fs',
                'app/map-ready': false,
                'app/current-map': null,
                'app/current-layer': null,
                // 'app/current-feature': null,
                'app/selected-features': [],
                'app/route': config.args,
                'app/table/selected': false,
                'component/legend': {
                    currentPage: 'info',
                },

                'component/menu': {
                    folded: true,
                },

                'component/home': defaultHomeConfigTile(),

                'component/table': defaultTableState(),
                'component/table/extract': initialTableState(),

                'component/legend/expand-image': false,

                'component/legend/show-wms-legend': false,

                'component/legend/webservices': {
                    folded: true,
                    url: '',
                    layers: [],
                },

                'component/legend/geocoder': defaultToolGeocoder(),

                'component/legend/positioner': {
                    point: {
                        latitude: 0,
                        longitude: 0,
                    },
                },

                'component/legend/share': {
                    withView: false,
                },

                'component/harvest/geometry': null,
                'component/harvest/geometry-type': null,
                'component/harvest/results': [],
                'component/harvest/minimap': {},

                'component/button': {},
                'component/bookmark/current-index': null,

                'port/map/scale': {
                    count: 0,
                    unit: '',
                    width: 0,
                },

                'port/map/view': {
                    dirty: 'geo',
                    srs: parseCRSName(mapCRSFromConfig(config)),
                    center: getCenter(config.defaultExtent),
                    rotation: 0,
                    zoom: 9,
                    feature: null,
                    features: [],
                    extent: null,
                },

                'port/map/interaction': defaultInteraction(),
                'port/map/printRequest': defaultPrintRequest(),
                'port/map/printResponse': defaultPrintResponse(),

                'component/print': defaultPrintState(),

                'port/map/loading': [],
                'data/layers': {},
                'data/maps': remoteNone,
                'data/alias': [],
                'data/categories': [],
                'data/roles': [],
                'data/datasetMetadata': {},
                'data/attachments': [],
                'data/baselayers': [],
                'data/baselayercategories': [],
                'data/links': {},
                'data/layerinfo-list': [],

                'remote/errors': {},

                'data/features/stream': initialStreamingState(),
                'data/users': [],
                'data/poc': [],
            };

            try {
                const start = source<IShape>(['app/lang']);
                const store = start(initialState);
                configure(store);
                const app = App(config.args)(store);
                app();
            } catch (err) {
                displayException(`${err}`);
            }
        },
    );
};

logger('loaded');
